import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalizationProvider, DatePicker } from '@mui/lab';
import {
  Fade,
  SxProps,
  Card,
  CardContent,
  Typography,
  TableContainer,
  Table,
  TableBody,
  Box,
  TablePagination,
  FormControlLabel,
  Switch,
  Grid,
  FormControl,
  TextFieldProps,
} from '@mui/material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { Moment } from 'moment';

import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import { useAppSelector } from '../../../../../../hooks';
import TransactionLogTableRow from './TransactionLogTableRow';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/admin/UserListDropdown';
import { TPTextField } from '../../../../../../components/Inputs';
import {
  adminGetTransactionLogs,
  getTransactionLogsState,
  ITransactionLog,
  resetAdminTransactionLogs,
  TAdminGetTransactionLogs,
} from '../../../../../../store/V2/TransactionLogsSlice';
import AdminNotesModal from './AdminNotesModal';

type TState = {
  selecteduser: TUserSelectOption | null;
  startDate: Moment | null;
  endDate: Moment | null;
};

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  userId?: string;
  startDate?: string;
  endDate?: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const tableCardContainerStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const defaultListLimit = 30;
const defaultOrderBy = 'date';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultFilterData: TFilterData = {
  offset: 0,
  limit: defaultListLimit,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
};

const defaultState: TState = {
  selecteduser: null,
  startDate: null,
  endDate: null,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'date',
    label: 'Date',
    sortable: true,
    width: 150,
  },
  {
    id: 'user',
    label: 'User',
    sortable: false,
  },
  {
    id: 'description',
    label: 'Description',
    sortable: true,
  },
  {
    id: 'creditChange',
    label: 'Credit Change',
    sortable: true,
  },
  {
    id: 'currentBalance',
    label: 'Balance',
    sortable: true,
  },
  {
    id: 'adminNotes',
    label: 'Admin Notes',
    sortable: false,
    width: 100,
    maxWidth: 100,
  },
  {
    id: 'updatedBy',
    label: 'Updated By',
    sortable: false,
  },
];

const TransactionLogsTab: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const transactionLogsState = useAppSelector(getTransactionLogsState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [viewAdminNotesModalData, setViewAdminNotesModalData] = useState<{
    open: boolean;
    transactionLog?: ITransactionLog;
  }>({ open: false });

  let transactionLogList: ITransactionLog[] = [];
  let totalCount = 0;
  let selectedTransactionLogForAdminNotesModal: ITransactionLog | undefined;

  if (transactionLogsState.adminTransactionLogs) {
    totalCount = transactionLogsState.adminTransactionLogs.totalDocs || 0;

    if (
      transactionLogsState.adminTransactionLogs.list &&
      transactionLogsState.adminTransactionLogs.list.length
    ) {
      transactionLogList = transactionLogsState.adminTransactionLogs.list || [];
    }
  }

  if (viewAdminNotesModalData.open && viewAdminNotesModalData.transactionLog) {
    selectedTransactionLogForAdminNotesModal = transactionLogList.find(
      (item) => item._id === viewAdminNotesModalData.transactionLog?._id
    );
  }

  useEffect(() => {
    getTransactionLogListData({ ...defaultFilterData });

    return () => {
      dispatch(resetAdminTransactionLogs());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getTransactionLogListData = (filterDataAttributes: TFilterData) => {
    const apiData: TAdminGetTransactionLogs = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.userId) {
      apiData.userId = filterDataAttributes.userId;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.endDate) {
      apiData.endDate = filterDataAttributes.endDate;
    }

    dispatch(adminGetTransactionLogs({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getTransactionLogListData({
      ...filterData,
      sortKey: id,
      sortValue: newSortValue,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getTransactionLogListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getTransactionLogListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  return (
    <Fade in>
      <div>
        <Card sx={tableCardContainerStyles}>
          <CardContent style={{ padding: '20px 0' }}>
            <div style={{ padding: '20px' }}>
              <div>
                <Typography fontSize="24px" fontWeight={600}>
                  Transaction Logs
                </Typography>
              </div>
            </div>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <div style={{ marginBottom: '20px', padding: '0 20px' }}>
                <Grid container columnSpacing="16px" rowSpacing="20px">
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <DatePicker
                        clearable
                        value={state.startDate}
                        label="Start Date"
                        inputFormat="YYYY-MM-DD"
                        onChange={(date) => {
                          updateStateAttributes({
                            startDate: date,
                          });

                          getTransactionLogListData({
                            ...filterData,
                            startDate: date
                              ? date.clone().startOf('day').toISOString()
                              : undefined,
                          });
                        }}
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...textFieldProps}
                            size="small"
                            placeholder="Start Date"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <DatePicker
                        clearable
                        value={state.endDate}
                        label="End Date"
                        inputFormat="YYYY-MM-DD"
                        onChange={(date) => {
                          updateStateAttributes({
                            endDate: date,
                          });

                          getTransactionLogListData({
                            ...filterData,
                            endDate: date
                              ? date.clone().endOf('day').toISOString()
                              : undefined,
                          });
                        }}
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...textFieldProps}
                            size="small"
                            placeholder="End Date"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <UserListDropdown
                        selectedItem={state.selecteduser}
                        handleChange={(e, value) => {
                          updateStateAttributes({
                            selecteduser: value,
                          });

                          getTransactionLogListData({
                            ...filterData,
                            userId: value ? value.value : undefined,
                          });
                        }}
                        textFieldProps={{
                          label: 'User',
                          placeholder: 'Filter by User',
                          ...textFieldProps,
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </div>
            </LocalizationProvider>
            <TableContainer
              sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
            >
              <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={tableData.order}
                  orderBy={tableData.orderBy}
                  headLabels={tableHeaderLabels}
                  rowCount={0}
                  numSelected={tableData.selected.length}
                  onSort={handleSort}
                />
                <TableBody>
                  {transactionLogList.map((row) => (
                    <TransactionLogTableRow
                      key={row._id}
                      transactionLog={row}
                      viewAdminNotes={() =>
                        setViewAdminNotesModalData({
                          open: true,
                          transactionLog: row,
                        })
                      }
                    />
                  ))}

                  {transactionLogsState.status !== 'loading' &&
                  !transactionLogList.length ? (
                    <TableNoData title="No transaction logs are available" />
                  ) : null}

                  {transactionLogsState.status === 'loading' &&
                  !transactionLogList.length ? (
                    <TableLoader />
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={defaultRowsPerPageOptions}
                component="div"
                count={totalCount}
                rowsPerPage={tableData.rowsPerPage}
                page={tableData.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleRowsPerPageChange}
                ActionsComponent={TablePaginationActions}
              />

              <FormControlLabel
                label="Dense"
                sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                control={
                  <Switch
                    checked={tableData.dense}
                    onChange={tableData.onChangeDense}
                  />
                }
              />
            </Box>
            {/* MODALS */}
            {viewAdminNotesModalData.open &&
            viewAdminNotesModalData.transactionLog &&
            selectedTransactionLogForAdminNotesModal ? (
              <AdminNotesModal
                open={viewAdminNotesModalData.open}
                transactionLog={selectedTransactionLogForAdminNotesModal}
                onClose={() =>
                  setViewAdminNotesModalData({
                    open: false,
                  })
                }
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </Fade>
  );
};

export default TransactionLogsTab;
